// ─────────────────────────────────────────────────────────────────────
// ThemeContext (v2)
//
// Two independent axes:
//   • themeId : 'editorial-gold' | 'magenta-noir' | 'crimson-premium' | 'sapphire-modern' | 'royal-velvet'
//   • mode    : 'dark' | 'light'
//
// Effective tokens = theme[mode === 'light' ? 'tokensLight' : 'tokens']
//
// Sources of truth (highest wins):
//   1. localStorage 'snt:theme' / 'snt:mode'  — per-device user choice
//   2. server-side admin global override (settings.theme_id / settings.mode)
//   3. system preference (prefers-color-scheme) on first visit — only when themes.json sets followSystem (r7.6.3: off)
//   4. themes.json default ('editorial-gold' + 'dark')
//
// Mutates document root: --snt-{token} CSS vars + data-theme + data-mode.
// Fonts: dynamically injects Google Fonts <link> per active theme.
//
// r7.6 additions
//   • custom themes — /public/settings.custom_themes (superadmin palette editor) are registered beside the
//     five presets at runtime: `themes` lists both, findTheme() resolves both, active_theme_id may name one.
//   • tenant palette — TenantProvider calls setTenantPalette({ primary, secondary, mode, theme }) for a
//     non-platform tenant: `theme` picks the base look (preset or custom), `mode` the default mode, and
//     primary/secondary are derived into accent / surface tokens on top (accent-fg computed for AA).
//     Resolution: user's local choice > tenant palette > admin global > system > default.
// ─────────────────────────────────────────────────────────────────────

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import themesRegistry from './themes.json';
import api from '../api/client';
import { bestForeground, shade, HEX } from '../lib/contrast';   // r7.6 tenant palette derivation

const STORAGE_THEME = 'snt:theme';
const STORAGE_MODE  = 'snt:mode';
export const OPTIONAL_TOKENS = ['gradient', 'band', 'accent-2'];   // r7.6.3: presets may carry them; CSS falls back when absent
const FONT_LINK_ID  = 'snt-theme-font-link';

const ThemeContext = createContext(null);

// ─── Runtime registry (presets + custom) ─────────────────────────────
// r7.6: custom themes arrive from /public/settings; the module-level list keeps findTheme() synchronous
// for the many call sites that use it outside React state.
const PRESETS = themesRegistry.themes;
let CUSTOM = [];
const listeners = new Set();
export function registerCustomThemes(list) {
  const clean = Array.isArray(list) ? list.filter((t) => t && /^custom-[a-z0-9-]+$/.test(t.id) && t.tokens && typeof t.tokens === 'object') : [];
  CUSTOM = clean;
  listeners.forEach((fn) => { try { fn(allThemes()); } catch { /* ignore */ } });
}
export function allThemes() { return [...PRESETS, ...CUSTOM]; }
export function isCustomThemeId(id) { return /^custom-[a-z0-9-]+$/.test(String(id || '')); }

// ─── Helpers ──────────────────────────────────────────────────────────
function findTheme(id) {
  return allThemes().find((t) => t.id === id) || null;
}
function defaultTheme() {
  return findTheme(themesRegistry.default) || themesRegistry.themes[0];
}
function defaultMode() {
  return themesRegistry.defaultMode || 'dark';
}

function effectiveTokens(theme, mode) {
  if (!theme) return {};
  if (mode === 'light' && theme.tokensLight) return theme.tokensLight;
  return theme.tokens || {};
}

/**
 * r7.6 — derive the token overrides a tenant palette implies.
 *   primary   → accent (+ accent-deep darker, accent-soft lighter, accent-fg black/white by contrast)
 *   secondary → bg-1 surface (+ bg-2 / bg-3 lightened steps) — a dark secondary on a light base is
 *               ignored (would fail AA), and vice versa; the tenant keeps its base look instead.
 */
export function tenantTokenOverrides(palette, mode) {
  if (!palette || typeof palette !== 'object') return {};
  const out = {};
  const primary = HEX.test(String(palette.primary || '')) ? String(palette.primary).toUpperCase() : null;
  const secondary = HEX.test(String(palette.secondary || '')) ? String(palette.secondary).toUpperCase() : null;
  if (primary) {
    out.accent = primary;
    out['accent-deep'] = shade(primary, -0.18);
    out['accent-soft'] = shade(primary, 0.22);
    out['accent-fg'] = bestForeground(primary);
  }
  if (secondary) {
    const secondaryIsDark = bestForeground(secondary) === '#FFFFFF';
    if ((mode === 'light') !== secondaryIsDark) {   // dark surface on a dark base, light surface on a light base
      out['bg-1'] = secondary;
      out['bg-2'] = shade(secondary, mode === 'light' ? -0.04 : 0.06);
      out['bg-3'] = shade(secondary, mode === 'light' ? -0.08 : 0.12);
    }
  }
  return out;
}

// r7.10 (MOV-03): legacy names the r6 pages read → the token that carries the colour
export const LEGACY_ALIASES = [['gold', 'accent'], ['gold-fg', 'accent-fg'], ['rose', 'danger']];

function applyTheme(theme, mode, overrides = {}) {
  if (!theme) return;
  const root = document.documentElement;
  const tokens = { ...effectiveTokens(theme, mode), ...overrides };

  // Set CSS variables (both --token and --snt-token forms for compat)
  Object.entries(tokens).forEach(([k, v]) => {
    root.style.setProperty(`--${k}`, v);
    root.style.setProperty(`--snt-${k}`, v);
  });
  // r7.6.3: optional tokens (Showz Live's gradient / band / accent-2) must not linger when the next theme lacks them
  for (const k of OPTIONAL_TOKENS) if (tokens[k] === undefined) { root.style.removeProperty(`--${k}`); root.style.removeProperty(`--snt-${k}`); }
  // r7.10 (QA MOV-03): the r6 pages — movies, festivals, attractions, workshops and a dozen console screens — style
  // their primary buttons, chips and rules with `var(--gold)` and their errors with `var(--rose)`, names no theme has
  // ever defined. The property resolved to nothing, so the selected date chip vanished and "Continue to payment"
  // painted black on black. Both are aliases of tokens every theme does carry.
  for (const [alias, src] of LEGACY_ALIASES) {
    const v = tokens[src];
    if (v !== undefined) { root.style.setProperty(`--${alias}`, v); root.style.setProperty(`--snt-${alias}`, v); }
  }
  // Fonts
  if (theme.fonts) {
    if (theme.fonts.serif) root.style.setProperty('--font-serif', `'${theme.fonts.serif}', Georgia, serif`);
    if (theme.fonts.sans)  root.style.setProperty('--font-sans',  `'${theme.fonts.sans}', -apple-system, BlinkMacSystemFont, sans-serif`);
    if (theme.fonts.mono)  root.style.setProperty('--font-mono',  `'${theme.fonts.mono}', ui-monospace, monospace`);
  }
  // Identifier attributes
  root.setAttribute('data-theme', theme.id);
  root.setAttribute('data-mode',  mode);
  root.style.colorScheme = mode === 'light' ? 'light' : 'dark';
}

function injectFontLink(theme) {
  if (!theme || !theme.fonts) return;
  const families = [];
  if (theme.fonts.serif) {
    const s = encodeURIComponent(theme.fonts.serif);
    families.push(`${s}:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500`);
  }
  if (theme.fonts.sans) {
    const s = encodeURIComponent(theme.fonts.sans);
    families.push(`${s}:wght@300;400;500;600;700`);
  }
  if (!families.length) return;
  const url =
    'https://fonts.googleapis.com/css2?' +
    families.map((f) => `family=${f}`).join('&') +
    '&display=swap';

  let link = document.getElementById(FONT_LINK_ID);
  if (!link) {
    link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  if (link.href !== url) link.href = url;
}

function readStored(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeStored(key, val) {
  try {
    if (val) localStorage.setItem(key, val);
    else localStorage.removeItem(key);
  } catch { /* ignore */ }
}

function detectSystemMode() {
  try {
    if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
    return 'dark';
  } catch {
    return 'dark';
  }
}

// ─── Provider ─────────────────────────────────────────────────────────
export function ThemeProvider({
  children,
  initialAdminThemeId = null,
  initialAdminMode = null,
}) {
  // r7.6: presets + custom (re-rendered when registerCustomThemes() runs)
  const [all, setAll] = useState(() => allThemes());
  useEffect(() => { listeners.add(setAll); return () => listeners.delete(setAll); }, []);

  // Admin global state (from server)
  const [adminThemeId, setAdminThemeId] = useState(initialAdminThemeId);
  const [adminMode,    setAdminMode]    = useState(initialAdminMode);

  // r7.6 tenant palette (TenantProvider) — { primary, secondary, mode, theme } or null on the platform
  const [tenantPalette, setTenantPaletteState] = useState(null);

  // Local user state (from localStorage)
  const [localThemeId, setLocalThemeId] = useState(() => readStored(STORAGE_THEME));
  const [localMode,    setLocalMode]    = useState(() => readStored(STORAGE_MODE));

  // Resolve effective theme + mode (r7.6: tenant palette sits between the user's choice and the admin global)
  const tenantThemeId = tenantPalette?.theme && findTheme(tenantPalette.theme) ? tenantPalette.theme : null;
  const tenantMode = tenantPalette?.mode === 'dark' || tenantPalette?.mode === 'light'
    ? tenantPalette.mode
    : (tenantPalette?.mode === 'auto' ? detectSystemMode() : null);
  const activeId =
    localThemeId ||
    tenantThemeId ||
    adminThemeId ||
    themesRegistry.default;

  // First-visit mode resolution: localStorage → tenant → admin → system → default
  const activeMode =
    localMode ||
    tenantMode ||
    adminMode ||
    // r7.6.3: the brand look is dark-first — a first visit lands on the theme's default mode; the OS scheme is
    // consulted only when the registry opts in (`followSystem: true`). Light stays one toggle away.
    (typeof window !== 'undefined' && themesRegistry.followSystem === true && readStored(STORAGE_MODE) === null
      ? detectSystemMode()
      : null) ||
    defaultMode();

  const active = findTheme(activeId) || defaultTheme();
  const tenantOverrides = useMemo(() => tenantTokenOverrides(tenantPalette, activeMode), [tenantPalette, activeMode]);

  // Apply on every change
  useEffect(() => {
    applyTheme(active, activeMode, tenantOverrides);
    injectFontLink(active);
  }, [active, activeMode, tenantOverrides, all]);

  // Fetch admin global theme + mode on mount (best-effort)
  useEffect(() => {
    if (initialAdminThemeId !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/public/settings');
        if (cancelled) return;
        const s = data?.settings || {};
        if (Array.isArray(s.custom_themes)) registerCustomThemes(s.custom_themes);   // r7.6 before the id check
        if (s.active_theme_id && findTheme(s.active_theme_id)) {
          setAdminThemeId(s.active_theme_id);
        }
        if (s.active_theme_mode === 'light' || s.active_theme_mode === 'dark') {
          setAdminMode(s.active_theme_mode);
        }
        // Expose the "allow user toggle" flag for header toggle visibility
        if (typeof s.allow_user_mode_toggle === 'boolean') {
          window.__SNT_ALLOW_USER_MODE_TOGGLE__ = s.allow_user_mode_toggle;
        }
      } catch {
        // silent fallback to default theme
      }
    })();
    return () => { cancelled = true; };
  }, [initialAdminThemeId]);

  // Listen for system pref changes (only matters if user hasn't picked a mode)
  useEffect(() => {
    if (localMode) return;
    if (typeof window === 'undefined') return;
    let mql;
    try {
      mql = window.matchMedia('(prefers-color-scheme: light)');
      const handler = () => {
        setLocalMode((current) => current); // trigger rerender; activeMode will recompute
      };
      mql.addEventListener?.('change', handler);
      return () => mql.removeEventListener?.('change', handler);
    } catch { /* ignore */ }
  }, [localMode]);

  // ── Public API ──
  const setUserTheme = useCallback((id) => {
    if (id && !findTheme(id)) {
      console.warn('[snt theme] unknown theme id:', id);
      return;
    }
    setLocalThemeId(id || null);
    writeStored(STORAGE_THEME, id);
  }, []);

  const clearUserTheme = useCallback(() => {
    setLocalThemeId(null);
    writeStored(STORAGE_THEME, null);
  }, []);

  const setUserMode = useCallback((mode) => {
    if (mode !== 'dark' && mode !== 'light' && mode !== null) {
      console.warn('[snt theme] mode must be dark|light|null, got:', mode);
      return;
    }
    setLocalMode(mode);
    writeStored(STORAGE_MODE, mode);
  }, []);

  const toggleMode = useCallback(() => {
    setLocalMode((curr) => {
      const next = (curr || activeMode) === 'dark' ? 'light' : 'dark';
      writeStored(STORAGE_MODE, next);
      return next;
    });
  }, [activeMode]);

  const clearUserMode = useCallback(() => {
    setLocalMode(null);
    writeStored(STORAGE_MODE, null);
  }, []);

  // r7.6 — TenantProvider hands over the tenant's palette (null on the platform tenant)
  const setTenantPalette = useCallback((palette) => {
    setTenantPaletteState(palette && typeof palette === 'object' ? palette : null);
  }, []);

  const setGlobalAdminTheme = useCallback(async ({ theme_id, mode }) => {
    if (theme_id && !findTheme(theme_id)) throw new Error('unknown theme');
    if (mode && mode !== 'dark' && mode !== 'light') throw new Error('mode must be dark or light');
    await api.put('/admin/settings/theme', { theme_id, mode });
    if (theme_id) setAdminThemeId(theme_id);
    if (mode) setAdminMode(mode);
  }, []);

  const value = useMemo(
    () => ({
      // active resolution
      active,
      activeId: active.id,
      activeMode,
      effectiveTokens: { ...effectiveTokens(active, activeMode), ...tenantOverrides },
      // listings
      themes: all,
      presets: PRESETS,
      customThemes: all.filter((t) => isCustomThemeId(t.id)),
      // r7.6 tenant palette
      tenantPalette, setTenantPalette, tenantOverrides,
      // sources
      adminThemeId, adminMode,
      localThemeId, localMode,
      // mutations
      setUserTheme, clearUserTheme,
      setUserMode, clearUserMode, toggleMode,
      setGlobalAdminTheme,
      // helpers
      findTheme, registerCustomThemes, isCustomThemeId,
    }),
    [active, activeMode, all, adminThemeId, adminMode, localThemeId, localMode, tenantPalette, tenantOverrides,
     setUserTheme, clearUserTheme, setUserMode, clearUserMode, toggleMode, setGlobalAdminTheme, setTenantPalette]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────
/**
 * r7.8 — the optional read, same shape as useTenantOptional(). Four components wanted the theme
 * "only if there is a provider" and wrote `try { useTheme() } catch {}`, which is a hook behind a
 * conditional: react-hooks/rules-of-hooks flags it, and it is only safe because useTheme() either
 * always throws for a given tree or never does. This does the job without the try/catch.
 */
export function useThemeOptional() {
  return useContext(ThemeContext);
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
